import navigationLinks, {
  NavigationLinkConfig,
} from "./navigationLinks";

const STOP_WORDS = new Set([
  "go",
  "goto",
  "to",
  "the",
  "please",
  "show",
  "me",
  "open",
  "take",
  "navigate",
  "for",
  "my",
  "a",
  "an",
  "into",
  "on",
  "page",
]);

interface MatchOptions {
  links?: NavigationLinkConfig[];
  limit?: number;
}

export interface NavigationMatch extends NavigationLinkConfig {
  score: number;
}

function sanitize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function simpleStem(token: string): string {
  if (token.endsWith("ies")) {
    return token.slice(0, -3) + "y";
  }
  if (token.endsWith("es")) {
    return token.slice(0, -2);
  }
  if (token.endsWith("s") && token.length > 3) {
    return token.slice(0, -1);
  }
  return token;
}

function tokenize(query: string): string[] {
  return sanitize(query)
    .split(" ")
    .map(simpleStem)
    .filter((token) => token && !STOP_WORDS.has(token));
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const matrix = Array.from({ length: a.length + 1 }, (_, i) =>
    Array(b.length + 1).fill(0)
  );

  for (let i = 0; i <= a.length; i += 1) {
    matrix[i][0] = i;
  }

  for (let j = 0; j <= b.length; j += 1) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }

  return matrix[a.length][b.length];
}

function buildCandidateStrings(link: NavigationLinkConfig): string[] {
  const pool = [
    link.title,
    link.description,
    link.route.replace("/access/", ""),
    ...link.keywords,
  ];

  if (link.speechPhrases) {
    pool.push(...link.speechPhrases);
  }

  return pool.map(sanitize).filter(Boolean);
}

function scoreTokenAgainstCandidates(
  token: string,
  candidates: string[]
): number {
  let bestScore = 0;

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (candidate.includes(token)) {
      // Higher score if we find an exact token match
      bestScore = Math.max(bestScore, 3);
      continue;
    }

    const candidateTokens = candidate.split(" ");
    if (candidateTokens.some((word) => word.startsWith(token))) {
      bestScore = Math.max(bestScore, 2);
      continue;
    }

    const fuzzyHit = candidateTokens.some(
      (word) => Math.min(word.length, token.length) > 3 && levenshtein(word, token) <= 1
    );

    if (fuzzyHit) {
      bestScore = Math.max(bestScore, 1);
    }
  }

  return bestScore;
}

export function matchNavigationLinks(
  query: string,
  options?: MatchOptions
): NavigationMatch[] {
  const normalizedTokens = tokenize(query);
  if (normalizedTokens.length === 0) return [];

  const activeLinks = options?.links ?? navigationLinks;
  const matches = activeLinks
    .map((link) => {
      const candidates = buildCandidateStrings(link);
      const tokenScore = normalizedTokens.reduce((score, token) => {
        return score + scoreTokenAgainstCandidates(token, candidates);
      }, 0);

      return { ...link, score: tokenScore };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score || a.title.localeCompare(b.title));

  const limit = options?.limit ?? 5;
  return matches.slice(0, limit);
}

export function normalizeQueryDisplay(query: string): string {
  const tokens = tokenize(query);
  if (!tokens.length) return "";
  return tokens.join(" ");
}

